import { BaseService } from './BaseService';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'https://api.daily2k.ng/api')
  .replace(/\/api\/?$/, '')
  .replace(/\/$/, '');

export interface Artisan {
  id: string;
  name: string;
  phone: string;
  email?: string;
  rating?: number;
  verified?: boolean;
}

export interface GuarantorDetails {
  name?: string;
  occupation?: string;
  relationship?: string;
  phone?: string;
}

/** Summary shape returned by the LIST endpoint */
export interface Verification {
  id: string;
  artisan: Artisan;
  type: 'kyc' | 'identity' | 'bank' | 'selfie' | 'guarantor';
  /** Normalised status returned by the list endpoint */
  status: 'pending' | 'pending_admin_review' | 'approved' | 'rejected' | 'expired';
  /** Raw DB status — may be pending_admin_review, pending_skill_info, etc. */
  raw_status?: string;
  submitted_at: string;
  reviewed_at?: string;
  rejection_reason?: string;
  documents: Record<string, string> | null | undefined;
  guarantor?: GuarantorDetails;
  reviewed_by?: string;
}

/** Full detail shape returned by GET /admin/verifications/{id}/ */
export interface VerificationDetail {
  id: string;
  artisan_name: string;
  artisan_email: string;
  artisan_phone: string;
  service_category: string;
  status: string;
  status_display: string;
  // Step 1 — Profile photo
  professional_photo_url: string | null;
  // Step 2 — Identity
  nin_display: string;          // masked  e.g. "***1234"
  nin_number: string;
  id_photo_url: string | null;
  id_type: string;
  // Step 3 — Guarantor
  guarantor_full_name: string;
  guarantor_phone_number: string;
  guarantor_occupation: string;
  guarantor_relationship: string;
  guarantor_consent: boolean;
  // Step completion flags
  skill_info_completed: boolean;
  identity_completed: boolean;
  guarantor_completed: boolean;
  // Admin review
  reviewed_at: string | null;
  reviewed_by_name: string | null;
  review_notes: string;
  rejection_reason: string;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  action: string;
  action_display: string;
  performed_by_name: string | null;
  notes: string;
  timestamp: string;
}

export interface VerificationDetailResponse {
  verification: VerificationDetail;
  audit_logs: AuditLog[];
}

export interface VerificationListResponse {
  count: number;
  verifications: Verification[];
}

// ✅ Extends BaseService to eliminate duplicate code
class VerificationService extends BaseService {
  private getStatusDisplay(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pending Review',
      pending_admin_review: 'Pending Review',
      pending_skill_info: 'Awaiting Skills',
      pending_identity: 'Awaiting ID',
      pending_guarantor: 'Awaiting Guarantor',
      approved: 'Approved',
      rejected: 'Rejected',
      expired: 'Expired',
    };
    return labels[status] || status;
  }

  private normalizeDetail(raw: any): VerificationDetailResponse {
    if (raw?.verification) {
      return {
        verification: {
          ...raw.verification,
          professional_photo_url: this.normalizeMediaUrl(raw.verification.professional_photo_url),
          id_photo_url: this.normalizeMediaUrl(raw.verification.id_photo_url),
        },
        audit_logs: raw.audit_logs || [],
      };
    }

    const artisan = raw?.artisan || {};
    const steps = raw?.steps || {};
    const guarantor = raw?.guarantor || {};
    const status = raw?.status || 'pending';

    return {
      verification: {
        id: raw?.id || '',
        artisan_name: raw?.artisan_name || artisan.name || '',
        artisan_email: raw?.artisan_email || artisan.email || '',
        artisan_phone: raw?.artisan_phone || artisan.phone || '',
        service_category: raw?.service_category || '',
        status,
        status_display: raw?.status_display || this.getStatusDisplay(status),
        professional_photo_url: this.normalizeMediaUrl(raw?.professional_photo_url),
        nin_display: raw?.nin_display || (raw?.nin_number ? `***${String(raw.nin_number).slice(-4)}` : ''),
        nin_number: raw?.nin_number || '',
        id_photo_url: this.normalizeMediaUrl(raw?.id_photo_url),
        id_type: raw?.id_type || '',
        guarantor_full_name: raw?.guarantor_full_name || guarantor.full_name || '',
        guarantor_phone_number: raw?.guarantor_phone_number || guarantor.phone || '',
        guarantor_occupation: raw?.guarantor_occupation || guarantor.occupation || '',
        guarantor_relationship: raw?.guarantor_relationship || guarantor.relationship || '',
        guarantor_consent: Boolean(raw?.guarantor_consent ?? guarantor.consent),
        skill_info_completed: Boolean(raw?.skill_info_completed ?? steps.skill_info),
        identity_completed: Boolean(raw?.identity_completed ?? steps.identity),
        guarantor_completed: Boolean(raw?.guarantor_completed ?? steps.guarantor),
        reviewed_at: raw?.reviewed_at || null,
        reviewed_by_name: raw?.reviewed_by_name || raw?.reviewed_by || null,
        review_notes: raw?.review_notes || '',
        rejection_reason: raw?.rejection_reason || '',
        approved_at: raw?.approved_at || null,
        created_at: raw?.created_at || raw?.submitted_at || '',
        updated_at: raw?.updated_at || '',
      },
      audit_logs: raw?.audit_logs || [],
    };
  }

  private normalizeMediaUrl(value?: string | null): string | null {
    if (!value) return null;

    const url = String(value).trim();
    if (!url) return null;

    if (url.startsWith('/')) {
      return `${API_ORIGIN}${url}`;
    }

    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/media/')) {
        return `${API_ORIGIN}${parsed.pathname}${parsed.search}`;
      }

      if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
        parsed.protocol = 'https:';
        return parsed.toString();
      }
    } catch {
      return url;
    }

    return url;
  }

  async getVerifications(
    status?: string,
    type?: string
  ): Promise<VerificationListResponse> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (type) params.append('type', type);

    return this.get(`/admin/verifications/`, {
      params: Object.fromEntries(params),
    });
  }

  async getVerificationDetail(verificationId: string): Promise<VerificationDetailResponse> {
    const raw = await this.get<any>(`/admin/verifications/${verificationId}/`);
    return this.normalizeDetail(raw);
  }

  async approveVerification(
    verificationId: string,
    notes: string = ''
  ): Promise<any> {
    return this.post(`/admin/verifications/${verificationId}/`, {
      action: 'approve',
      notes,
    });
  }

  async rejectVerification(
    verificationId: string,
    reason: string,
    notes: string = ''
  ): Promise<any> {
    return this.post(`/admin/verifications/${verificationId}/`, {
      action: 'reject',
      reason,
      notes,
    });
  }
}

export default new VerificationService();
